import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Settings as SettingsIcon, User, Users, Film, Zap } from "lucide-react";
import PageShell from "@/components/PageShell";
import OperatorCard from "@/components/OperatorCard";
import OperatorMiniCard from "@/components/OperatorMiniCard";
import OperatorWheel from "@/components/OperatorWheel";
import WheelIcon from "@/components/WheelIcon";
import ShortcutsNotice from "@/components/ShortcutsNotice";
import { rollLoadout } from "@/lib/loadout";
import { useSettings } from "@/contexts/SettingsContext";
import { ATTACKERS, DEFENDERS, getRoles } from "@/data/operators";
import { MAPS, getMapById, randomMap } from "@/data/maps";
import { pickRandom, pickRandomTeam, pickRandomTeamWithRoles } from "@/lib/roll";
import { playRollSequence, playReveal, playTick } from "@/lib/sound";
import { AlertTriangle } from "lucide-react";

const CINEMATIC_MS = 1800;
const CINEMATIC_STEPS = 22;
const INSTANT_MS = 180;

const TEAM_GRID = {
  2: "grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
};

export const Randomizer = () => {
  const {
    mode,
    rollMode,
    teamSize,
    soundOn,
    excluded,
    requiredRoles,
    loadoutToggles,
    selectedMapId,
    selectedSite,
  } = useSettings();

  const [operator, setOperator] = useState(null);
  const [team, setTeam] = useState([]);
  const [side, setSide] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [unfilledRoles, setUnfilledRoles] = useState([]);
  const [loadout, setLoadout] = useState(null); // single-mode loadout
  const [teamLoadouts, setTeamLoadouts] = useState([]); // array aligned with team
  const [rolledMap, setRolledMap] = useState(null); // map object used in this roll
  const [rolledSite, setRolledSite] = useState(null); // site string used in this roll
  const [wheelState, setWheelState] = useState(null); // { finalPick, loadout, map, site, side, spinKey } while wheel spinning
  const timersRef = useRef([]);

  // Resolve the map + site for this roll, honouring user selections.
  //  - "none"     → no map at all (banner hidden)
  //  - "random"   → roll a random map from the full list
  //  - specific   → use that map
  // Site is "random" unless a specific site is chosen AND the current map has it.
  const resolveMapAndSite = () => {
    if (!selectedMapId || selectedMapId === "none") return { map: null, site: null };
    const map = selectedMapId === "random" ? randomMap() : getMapById(selectedMapId);
    let site = selectedSite;
    if (!site || site === "random" || !map || !map.sites.includes(site)) {
      site = map && map.sites.length ? map.sites[Math.floor(Math.random() * map.sites.length)] : null;
    }
    return { map, site };
  };

  const clearTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };

  const pools = useMemo(() => {
    const filter = (arr) => arr.filter((o) => !excluded.has(o.id));
    return { attacker: filter(ATTACKERS), defender: filter(DEFENDERS) };
  }, [excluded]);

  const rollSingle = (targetSide) => {
    const pool = pools[targetSide];
    if (!pool || pool.length === 0) return;
    clearTimers();
    setSide(targetSide);
    setUnfilledRoles([]);
    setTeam([]);
    setTeamLoadouts([]);
    setLoadout(null);
    const { map, site } = resolveMapAndSite();
    setRolledMap(map);
    setRolledSite(site);
    const prevId = operator && side === targetSide ? operator.id : null;
    const finalPick = pickRandom(pool, prevId);
    const finalLoadout = rollLoadout(finalPick, targetSide, loadoutToggles, map);

    if (rollMode === "instant") {
      setRolling(true);
      if (soundOn) playTick();
      timersRef.current.push(
        window.setTimeout(() => {
          setOperator(finalPick);
          setLoadout(finalLoadout);
          setRolling(false);
        }, INSTANT_MS),
      );
      return;
    }

    if (rollMode === "wheel") {
      // Hand off to the wheel component; reveal on spin-end via handleWheelEnd
      setRolling(true);
      setOperator(null);
      if (soundOn) playRollSequence();
      setWheelState({
        finalPick,
        loadout: finalLoadout,
        side: targetSide,
        spinKey: Date.now(),
      });
      return;
    }

    setRolling(true);
    if (soundOn) playRollSequence();
    let lastId = prevId;
    for (let i = 1; i <= CINEMATIC_STEPS; i++) {
      const t = i / CINEMATIC_STEPS;
      const eased = 1 - Math.pow(1 - t, 2.4);
      const when = eased * CINEMATIC_MS;
      timersRef.current.push(
        window.setTimeout(() => {
          if (i === CINEMATIC_STEPS) {
            setOperator(finalPick);
            setLoadout(finalLoadout);
            setRolling(false);
            if (soundOn) playReveal();
          } else {
            let next;
            do {
              next = pool[Math.floor(Math.random() * pool.length)];
            } while (pool.length > 1 && next.id === lastId);
            lastId = next.id;
            setOperator(next);
          }
        }, when),
      );
    }
  };

  // Called by <OperatorWheel> when its CSS transition completes.
  const handleWheelEnd = () => {
    if (!wheelState) return;
    setOperator(wheelState.finalPick);
    setLoadout(wheelState.loadout);
    setRolling(false);
    setWheelState(null);
    if (soundOn) playReveal();
  };

  const rollTeam = (targetSide) => {
    const pool = pools[targetSide];
    if (!pool || pool.length === 0) return;
    clearTimers();
    setSide(targetSide);
    setOperator(null);
    setLoadout(null);
    const { map, site } = resolveMapAndSite();
    setRolledMap(map);
    setRolledSite(site);
    const roleDefs = getRoles(targetSide);
    const required = Array.from(requiredRoles[targetSide]);
    const { team: finalTeam, unfilledRoles: uf } = pickRandomTeamWithRoles(
      pool,
      teamSize,
      required,
      roleDefs,
    );
    const finalLoadouts = finalTeam.map((op) => rollLoadout(op, targetSide, loadoutToggles, map));

    if (rollMode === "instant") {
      setRolling(true);
      if (soundOn) playTick();
      timersRef.current.push(
        window.setTimeout(() => {
          setTeam(finalTeam);
          setTeamLoadouts(finalLoadouts);
          setUnfilledRoles(uf);
          setRolling(false);
        }, INSTANT_MS),
      );
      return;
    }

    setRolling(true);
    if (soundOn) playRollSequence();
    setUnfilledRoles([]);
    setTeamLoadouts([]);
    for (let i = 1; i <= CINEMATIC_STEPS; i++) {
      const t = i / CINEMATIC_STEPS;
      const eased = 1 - Math.pow(1 - t, 2.4);
      const when = eased * CINEMATIC_MS;
      timersRef.current.push(
        window.setTimeout(() => {
          if (i === CINEMATIC_STEPS) {
            setTeam(finalTeam);
            setTeamLoadouts(finalLoadouts);
            setUnfilledRoles(uf);
            setRolling(false);
            if (soundOn) playReveal();
          } else {
            const locked = Math.max(0, Math.floor(((t - 0.55) / 0.45) * teamSize));
            const randomSlice = pickRandomTeam(pool, teamSize);
            const hybrid = randomSlice.map((op, idx) =>
              idx < locked && finalTeam[idx] ? finalTeam[idx] : op,
            );
            setTeam(hybrid);
          }
        }, when),
      );
    }
  };

  const onRollAttacker = () => (mode === "single" ? rollSingle("attacker") : rollTeam("attacker"));
  const onRollDefender = () => (mode === "single" ? rollSingle("defender") : rollTeam("defender"));

  // Keyboard shortcuts: A = Attacker, D = Defender, R = re-roll last side.
  // Ignored when typing in an input, when modifier keys are held, or while a roll is in progress.
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (rolling) return;
      const key = (e.key || "").toLowerCase();
      if (key === "a") {
        e.preventDefault();
        onRollAttacker();
      } else if (key === "d") {
        e.preventDefault();
        onRollDefender();
      } else if (key === "r") {
        if (!side) return;
        e.preventDefault();
        if (side === "attacker") onRollAttacker();
        else onRollDefender();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rolling, side, soundOn, excluded, requiredRoles, teamSize, rollMode, loadoutToggles, selectedMapId, selectedSite]);

  const hasRolled = mode === "single" ? !!operator : team.length > 0;

  const headerRight = (
    <Link
      to="/settings"
      data-testid="randomise-to-settings-btn"
      className="flex items-center gap-2 border border-neutral-700 hover:border-white text-neutral-300 hover:text-white px-3 py-1.5 transition-colors"
      style={{ fontFamily: '"JetBrains Mono", monospace' }}
    >
      <SettingsIcon size={14} />
      <span className="text-[10px] tracking-[0.3em] uppercase font-bold hidden sm:inline">
        Settings
      </span>
    </Link>
  );

  return (
    <PageShell rightSlot={headerRight}>
      <section className="max-w-5xl mx-auto px-6 py-8 lg:py-12 space-y-6">
        {/* Title + compact status line */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div
              className="text-[10px] text-neutral-500 tracking-[0.3em] uppercase"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Operator Dispatch
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-1"
              style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            >
              Roll <span className="text-[#FFC800]">Operator</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill icon={mode === "single" ? <User size={12} /> : <Users size={12} />}>
              {mode === "single" ? "Single" : `Team · ${teamSize}`}
            </StatusPill>
            <StatusPill icon={rollMode === "cinematic" ? <Film size={12} /> : rollMode === "wheel" ? <WheelIcon size={12} /> : <Zap size={12} />}>
              {rollMode === "cinematic" ? "Cinematic" : rollMode === "wheel" ? "Wheel" : "Instant"}
            </StatusPill>
            {hasRolled && side && !rolling && (
              <div
                className="hidden sm:block text-[10px] text-neutral-500 tracking-[0.3em] uppercase"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
                data-testid="last-roll-indicator"
              >
                Click again to re-roll
              </div>
            )}
          </div>
        </div>

        {/* Desktop-only shortcuts notice */}
        <ShortcutsNotice />

        {/* Map / Site banner (shown after a roll) */}
        {(operator || team.length > 0) && rolledMap && (
          <div
            data-testid="map-banner"
            className="flex flex-wrap items-center gap-3 border border-neutral-800 bg-neutral-950/60 px-4 py-2"
          >
            <span
              className="text-[10px] tracking-[0.3em] uppercase text-[#FFC800] font-bold"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              MAP
            </span>
            <span
              className="text-white text-base font-black uppercase tracking-tight"
              style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
              data-testid="map-banner-name"
            >
              {rolledMap.name}
            </span>
            {rolledSite && (
              <>
                <span className="text-neutral-700">·</span>
                <span
                  className="text-[10px] tracking-[0.3em] uppercase text-[#FFC800] font-bold"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  SITE
                </span>
                <span
                  className="text-white text-sm font-bold tracking-wide"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  data-testid="map-banner-site"
                >
                  {rolledSite}
                </span>
              </>
            )}
          </div>
        )}

        {/* Reveal */}
        {mode === "single" ? (
          wheelState ? (
            <OperatorWheel
              pool={pools[wheelState.side] || []}
              finalPick={wheelState.finalPick}
              side={wheelState.side}
              spinKey={wheelState.spinKey}
              onSpinEnd={handleWheelEnd}
            />
          ) : (
            <OperatorCard
              operator={operator}
              side={side}
              rolling={rolling}
              rollMode={rollMode}
              loadout={loadout}
            />
          )
        ) : (
          <TeamReveal
            team={team}
            side={side}
            rolling={rolling}
            teamSize={teamSize}
            unfilledRoles={unfilledRoles}
            roleDefs={side ? getRoles(side) : []}
            teamLoadouts={teamLoadouts}
          />
        )}

        {/* Primary actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            data-testid="random-attacker-btn"
            disabled={rolling || pools.attacker.length === 0}
            onClick={onRollAttacker}
            className="group relative border-2 border-[#E67300] bg-[#E67300]/10 hover:bg-[#E67300] text-[#E67300] hover:text-black py-6 transition-all disabled:opacity-40 disabled:cursor-not-allowed active:translate-y-[1px]"
          >
            <div
              className="text-[10px] tracking-[0.3em] uppercase font-bold"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {mode === "team" ? `Random Team · ${teamSize}` : "Random"}
            </div>
            <div
              className="text-3xl font-black uppercase tracking-tight"
              style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            >
              Attacker
            </div>
          </button>
          <button
            data-testid="random-defender-btn"
            disabled={rolling || pools.defender.length === 0}
            onClick={onRollDefender}
            className="group relative border-2 border-[#1F88E8] bg-[#1F88E8]/10 hover:bg-[#1F88E8] text-[#1F88E8] hover:text-white py-6 transition-all disabled:opacity-40 disabled:cursor-not-allowed active:translate-y-[1px]"
          >
            <div
              className="text-[10px] tracking-[0.3em] uppercase font-bold"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {mode === "team" ? `Random Team · ${teamSize}` : "Random"}
            </div>
            <div
              className="text-3xl font-black uppercase tracking-tight"
              style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            >
              Defender
            </div>
          </button>
        </div>
      </section>
    </PageShell>
  );
};

const StatusPill = ({ icon, children }) => (
  <div
    className="inline-flex items-center gap-1.5 border border-neutral-800 bg-neutral-950/60 px-2.5 py-1 text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-300"
    style={{ fontFamily: '"JetBrains Mono", monospace' }}
  >
    {icon}
    {children}
  </div>
);

const TeamReveal = ({ team, side, rolling, teamSize, unfilledRoles = [], roleDefs = [], teamLoadouts = [] }) => {
  const showPlaceholder = rolling && (!team || team.length === 0);
  if (showPlaceholder) {
    return (
      <div
        data-testid="team-card-rolling"
        className="border border-neutral-800 bg-neutral-950/60 p-10 min-h-[360px] flex items-center justify-center"
      >
        <div
          className="text-neutral-400 text-xs tracking-[0.3em] uppercase animate-pulse"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          Dispatching team...
        </div>
      </div>
    );
  }
  if (!team || team.length === 0) {
    return (
      <div
        data-testid="team-card-empty"
        className="border border-neutral-800 bg-neutral-950/60 p-10 min-h-[360px] flex flex-col items-center justify-center text-center"
      >
        <div
          className="text-neutral-600 text-xs tracking-[0.3em] uppercase font-bold"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          Team Awaiting Deployment
        </div>
        <div
          className="mt-4 text-neutral-500 max-w-sm"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Roll a random squad of {teamSize} attackers or defenders.
        </div>
      </div>
    );
  }
  const accent = side === "attacker" ? "#E67300" : "#1F88E8";
  const gridCols = TEAM_GRID[team.length] || TEAM_GRID[5];
  const roleLabelMap = new Map(roleDefs.map((r) => [r.id, r.label]));

  return (
    <div className="space-y-3" data-testid="team-reveal">
      <div
        className="text-xs font-bold tracking-[0.3em] uppercase"
        style={{ color: accent, fontFamily: '"JetBrains Mono", monospace' }}
      >
        {side === "attacker" ? "ATTACK TEAM" : "DEFENSE TEAM"} · {team.length} OPERATORS
      </div>
      <div className={`grid ${gridCols} gap-3`}>
        {team.map((op, i) => (
          <OperatorMiniCard
            key={`${op.id}-${i}`}
            operator={op}
            side={side}
            index={i}
            loadout={!rolling ? teamLoadouts[i] : null}
          />
        ))}
      </div>
      {unfilledRoles.length > 0 && (
        <div
          data-testid="unfilled-roles-warning"
          className="mt-3 flex items-start gap-2 border border-[#FFC800]/40 bg-[#FFC800]/5 px-3 py-2"
        >
          <AlertTriangle size={14} className="text-[#FFC800] mt-0.5 shrink-0" />
          <div
            className="text-[11px] text-[#FFC800] tracking-wider leading-relaxed"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Could not fulfill:&nbsp;
            {unfilledRoles.map((rid, i) => (
              <span key={rid}>
                {roleLabelMap.get(rid) || rid}
                {i < unfilledRoles.length - 1 ? ", " : ""}
              </span>
            ))}
            . Reduce exclusions or increase squad size.
          </div>
        </div>
      )}
    </div>
  );
};

export default Randomizer;
